import { __assign, __awaiter, __generator, __read, __values } from "tslib";
import { Divider, mergeClasses } from '@fluentui/react-components';
import { useLazyLoadQuery, useMutation, useNovaEventing } from '@nova/react';
import { AppBarEvents } from '@office-start/appbar-events';
import React, { createRef, useCallback, useMemo, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { useDebouncedCallback } from 'use-debounce';
import { useAppBarStyles } from './AppBar.styles';
import { controlModelQuery, movePinPositionMutation, pinAppMutation, unpinAppMutation, } from './AppBar.types';
import { AppBarContextProvider } from './AppBarContext';
import { AppBarErrorBoundary } from './AppBarErrorBoundary';
import { AppBarImpression } from './AppBarImpression';
import { AppBarBottomMargin, AppBarDividerMaxHeightValue, AppBarVisualRefreshTopMargin, FlyoutTileID, MinTiles, TileDimensionDefaultValue, TileHeightMercuryValue, TileHeightWideValue, TileMarginDefaultValue, ViewMode, } from './Constants';
import { FlexPanel } from './FlexPanel';
import { KeyNavDir } from './FlexPanel.types';
import { FlyoutButton } from './FlyoutButton';
import useAppBarContext from './hooks/useAppBarContext';
import { useDynamicMediaQueryListener } from './hooks/useMediaQueryListener';
import { LaunchTile } from './LaunchTile';
import { LazyTileContextMenu } from './LazyTileContextMenu';
import { TileSelectionBar } from './TileSelectionBar';
/**
 * Navigation control that renders launch tiles that navigate to associated apps
 */
export var AppBarRender = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    var _x = _a.matchMedia, matchMedia = _x === void 0 ? window.matchMedia : _x, // Default to the browser window implementation
    _y = _a.customImportHandler, // Default to the browser window implementation
    customImportHandler = _y === void 0 ? function (p) { return p; } : _y, assignShortcutHandler = _a.assignShortcutHandler, unassignShortcutHandler = _a.unassignShortcutHandler, onAppTileHoverEnter = _a.onAppTileHoverEnter, onAppTileHoverLeave = _a.onAppTileHoverLeave, backgroundShade = _a.backgroundShade, forwardRef = _a.forwardRef, brandLogo = _a.brandLogo;
    var _z = __read(React.useState(false), 2), mediaQueryReady = _z[0], setMediaQueryReady = _z[1];
    var appBarContext = useAppBarContext();
    var appBarResizeObserverRef = useRef();
    var selectionBar = createRef();
    var contextMenuRef = useRef(null);
    var flyoutButtonRef = React.useRef(null);
    var appbarContainerRef = React.useRef();
    var appBarControlModelData = useLazyLoadQuery(controlModelQuery, {});
    var _0 = __read(useMutation(pinAppMutation), 1), pinM365Application = _0[0];
    var _1 = __read(React.useState(false), 2), dndInUse = _1[0], setDnDInUse = _1[1];
    var _2 = __read(React.useState(true), 2), dividerVisibleState = _2[0], setDividerVisibleState = _2[1];
    var controlModelData = useMemo(function () {
        var _a;
        if (appBarControlModelData.error) {
            throw appBarControlModelData.error;
        }
        else if (!(appBarControlModelData === null || appBarControlModelData === void 0 ? void 0 : appBarControlModelData.data)) {
            // Loading
            return null;
        }
        return (_a = appBarControlModelData.data) === null || _a === void 0 ? void 0 : _a.views.appBarControl;
    }, [appBarControlModelData.data, appBarControlModelData.error]);
    var pinApp = React.useCallback(function (applicationID) {
        var _a, _b, _c, _d;
        pinM365Application({
            variables: {
                applicationID: applicationID,
            },
            optimisticResponse: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableOptimisticMutations)
                ? {
                    // Generate an optimistic response for when pinning succeeds, in which case
                    // the newly pinned app's pinned index would get the max pinned index + 1 value.
                    pinM365Application: {
                        updatedM365Applications: (_d = (_c = (_b = (_a = appBarContext.appBarData) === null || _a === void 0 ? void 0 : _a.m365Apps) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.map(function (edge) {
                            var _a;
                            var clonedNode = __assign({}, edge.node);
                            if (((_a = edge.node) === null || _a === void 0 ? void 0 : _a.id) === applicationID) {
                                clonedNode.pinnedIndex =
                                    appBarContext.maxPinnedIndex !=
                                        null
                                        ? appBarContext.maxPinnedIndex +
                                            1
                                        : 0;
                            }
                            return clonedNode;
                        })) !== null && _d !== void 0 ? _d : [],
                        __typename: 'M365ApplicationPinStatusUpdateResult',
                    },
                }
                : undefined,
        });
    }, [
        pinM365Application,
        appBarContext.appBarData,
        appBarContext.maxPinnedIndex,
        controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableOptimisticMutations,
    ]);
    var _3 = __read(useMutation(unpinAppMutation), 1), unpinM365Application = _3[0];
    var unpinApp = React.useCallback(function (applicationID) {
        var _a, _b, _c, _d;
        unpinM365Application({
            variables: {
                applicationID: applicationID,
            },
            optimisticResponse: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableOptimisticMutations)
                ? {
                    // Generate an optimistic response for when unpinning succeeds, in which case
                    // the unpinned app's pinned index would get set to -1
                    unpinM365Application: {
                        updatedM365Applications: (_d = (_c = (_b = (_a = appBarContext.appBarData) === null || _a === void 0 ? void 0 : _a.m365Apps) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.map(function (edge) {
                            var _a;
                            var clonedNode = __assign({}, edge.node);
                            if (((_a = edge.node) === null || _a === void 0 ? void 0 : _a.id) === applicationID) {
                                clonedNode.pinnedIndex = -1;
                            }
                            return clonedNode;
                        })) !== null && _d !== void 0 ? _d : [],
                        __typename: 'M365ApplicationPinStatusUpdateResult',
                    },
                }
                : undefined,
        });
    }, [
        unpinM365Application,
        appBarContext.appBarData,
        controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableOptimisticMutations,
    ]);
    var _4 = __read(useMutation(movePinPositionMutation), 1), movePinnedM365Application = _4[0];
    var _5 = __read(React.useMemo(function () {
        var appBarItemIds = [];
        var appbarItemsByIdLookUp = {};
        // if there are no pinned items, return an empty array
        if (!appBarContext.pinnedItems) {
            return [appBarItemIds, appbarItemsByIdLookUp];
        }
        // push all of the pinned items into the array
        appBarContext.pinnedItems.map(function (item) {
            appBarItemIds.push(item.data.node.id);
            appbarItemsByIdLookUp[item.data.node.id] = item.data.node.name;
        });
        // if the user has a hoisted tile, we need to add this to the array of sortable items if it doesn't have a pinned index
        if (appBarContext.hoistedItem &&
            appBarContext.hoistedItem.data.node.pinnedIndex === undefined) {
            appBarItemIds.push(appBarContext.hoistedItem.data.node.id);
        }
        // return all sortable items for DnD
        return [appBarItemIds, appbarItemsByIdLookUp];
    }, [appBarContext.pinnedItems, appBarContext.hoistedItem]), 2), appBarItemsById = _5[0], appbarItemsByIdLookUp = _5[1];
    var movePinnedApp = React.useCallback(function (movingApplicationID, movingToApplicationID) {
        if (!appBarItemsById.includes(movingApplicationID) ||
            !appBarItemsById.includes(movingToApplicationID)) {
            return;
        }
        if (appBarContext.pinnedItems &&
            appBarContext.pinnedItems.length > 0) {
            // get the index of the application we are moving to
            var movingToIndex = appBarContext.pinnedItems.findIndex(function (item) {
                return item.data.node.id === movingToApplicationID;
            });
            if (movingToIndex === -1) {
                return;
            }
            // get the app tile we are moving to
            var movingToApplicationTile = appBarContext.pinnedItems[movingToIndex];
            if (!movingToApplicationTile) {
                return;
            }
            // we need the pinned index of the app tile we are moving to
            var movingToPinnedIndex = movingToApplicationTile.data.node.pinnedIndex;
            if (movingToPinnedIndex === undefined ||
                movingToPinnedIndex === null) {
                return;
            }
            // update the App Bar Context with the new index of the pinned item
            appBarContext.movePinnedItem(movingApplicationID, movingToIndex);
            // update the pinned indexes of the app tiles
            movePinnedM365Application({
                variables: {
                    applicationID: movingApplicationID,
                    newPinnedIndex: movingToPinnedIndex,
                },
                // We currently won't support a optimistic response here yet, until our Apollo partners
                // are ready to support drag and drop.
            });
        }
    }, [appBarContext, movePinnedM365Application, appBarItemsById]);
    var tileBehavior = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.tileBehavior;
    var linkBehavior = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.appAcquisitionLinkBehavior;
    var flyoutBehavior = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior;
    var _6 = __read([
        (_b = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutIcon) !== null && _b !== void 0 ? _b : undefined,
        (_c = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutIconSelected) !== null && _c !== void 0 ? _c : undefined,
    ], 2), flyoutIcon = _6[0], flyoutIconSelected = _6[1];
    var enableMicaV2DesktopStyles = (_d = ((tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.transparent) &&
        (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableMicaV2DesktopStyles))) !== null && _d !== void 0 ? _d : false;
    var enableMicaV2WebStyles = (_e = ((tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.transparent) &&
        (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableMicaV2WebStyles))) !== null && _e !== void 0 ? _e : false;
    var enableMercuryDesignStyles = (_f = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableMercuryDesignStyles) !== null && _f !== void 0 ? _f : false;
    var appTilePositionEnabled = (_g = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.appTilePositionEnabled) !== null && _g !== void 0 ? _g : false;
    var isAppbarWide = !!(tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.shouldShowLabel) && !enableMercuryDesignStyles;
    var enableAIHub = (_h = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableAIHub) !== null && _h !== void 0 ? _h : false;
    var enableVisualRefreshStyles = (_j = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableVisualRefreshStyles) !== null && _j !== void 0 ? _j : false;
    var enableBrandLogo = (_k = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.enableBrandLogo) !== null && _k !== void 0 ? _k : false;
    var styles = useAppBarStyles();
    var backgroundShadeStyle = backgroundShade === 'ThreeColumnView'
        ? styles.threeColumnViewBackground
        : styles.twoColumnViewBackground;
    var appBarStyles = mergeClasses(styles.appBar, backgroundShadeStyle, isAppbarWide && styles.wideAppBar, enableMercuryDesignStyles && styles.mercuryDesignAppBar);
    var dividerStyles = mergeClasses(styles.divider, backgroundShadeStyle, !!(tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.transparent) && styles.transparent);
    /**
     * Dynamic media query hook listener using tile heights as thresholds
     * and the divider and children as bottom padding
     */
    var mediaQuery = useDynamicMediaQueryListener(matchMedia);
    /**
     * Effect that passes the render data for variables that won't change
     * often to the dynamic media query hook
     */
    React.useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        if (mediaQueryReady) {
            return;
        }
        // At least one locked or pinned app DOM element ref needs to be available
        // before we can set the top margin for the media query
        if (!((_b = (_a = appBarContext.lockedItems) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.elementRef.current) &&
            !((_d = (_c = appBarContext.pinnedItems) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.elementRef.current)) {
            return;
        }
        var tileHeight = calculateTileHeight(isAppbarWide, enableMercuryDesignStyles);
        mediaQuery.setElementHeight(tileHeight);
        mediaQuery.setMinimumVisibleElementCount(MinTiles);
        mediaQuery.setTopMargin((_p = (_j = (_h = (_g = (_f = (_e = appBarContext.lockedItems) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.elementRef) === null || _g === void 0 ? void 0 : _g.current) === null || _h === void 0 ? void 0 : _h.getBoundingClientRect().top) !== null && _j !== void 0 ? _j : (_o = (_m = (_l = (_k = appBarContext.pinnedItems) === null || _k === void 0 ? void 0 : _k[0]) === null || _l === void 0 ? void 0 : _l.elementRef) === null || _m === void 0 ? void 0 : _m.current) === null || _o === void 0 ? void 0 : _o.getBoundingClientRect().top) !== null && _p !== void 0 ? _p : 0);
        setMediaQueryReady(true);
    }, [
        appBarContext.lockedItems,
        appBarContext.pinnedItems,
        enableMercuryDesignStyles,
        isAppbarWide,
        mediaQuery,
        mediaQueryReady,
        tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.shouldShowLabel,
    ]);
    /**
     * Callback to update the top margin value for the media query. Use debounced callback
     * to prevent "ResizeObserver loop completed with undelivered notifications" error
     */
    var appBarResizeCallback = useDebouncedCallback(function (entries) {
        var e_1, _a;
        try {
            for (var entries_1 = __values(entries), entries_1_1 = entries_1.next(); !entries_1_1.done; entries_1_1 = entries_1.next()) {
                var entry = entries_1_1.value;
                if (mediaQueryReady) {
                    mediaQuery.setTopMargin(entry.target.getBoundingClientRect().top);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (entries_1_1 && !entries_1_1.done && (_a = entries_1.return)) _a.call(entries_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }, 10);
    /**
     * Callback to sets up the resize observer for the AppBar element
     * so that the top margin can be updated when the AppBar position changes
     */
    var setAppBarRef = React.useCallback(function (element) {
        appbarContainerRef.current = element;
        if (!element) {
            return;
        }
        // disconnect any existing resize observer
        if (appBarResizeObserverRef.current) {
            appBarResizeObserverRef.current.disconnect();
        }
        appBarResizeObserverRef.current = new ResizeObserver(function (entries) {
            appBarResizeCallback(entries);
        });
        appBarResizeObserverRef.current.observe(element);
    }, [appBarResizeCallback]);
    /**
     * Effect that cleans up any existing resize observer on unmount
     */
    React.useEffect(function () {
        return function () {
            if (appBarResizeObserverRef.current) {
                appBarResizeObserverRef.current.disconnect();
            }
        };
    }, []);
    /**
     * Effect that sets bottom padding value for media query hook
     */
    React.useEffect(function () {
        var tileHeight = calculateTileHeight(isAppbarWide, enableMercuryDesignStyles);
        var totalSlidingPanelTileHeight = tileHeight;
        // add the extra tile height if there is a hoisted item
        if (appBarContext.hoistedItem) {
            totalSlidingPanelTileHeight += tileHeight;
        }
        // add tile height for each anchored item
        if (appBarContext.anchoredItems &&
            appBarContext.anchoredItems.length > 0) {
            totalSlidingPanelTileHeight +=
                tileHeight * appBarContext.anchoredItems.length;
        }
        if (enableVisualRefreshStyles) {
            mediaQuery.setTopMargin(AppBarVisualRefreshTopMargin);
        }
        mediaQuery.setBottomPadding(AppBarDividerMaxHeightValue +
            totalSlidingPanelTileHeight +
            AppBarBottomMargin);
    }, [
        appBarContext.anchoredItems,
        appBarContext.hoistedItem,
        enableMercuryDesignStyles,
        isAppbarWide,
        mediaQuery,
        tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.shouldShowLabel,
    ]);
    /**
     * Sets the number of possible elements that can be visible to the media query hook
     */
    React.useEffect(function () {
        var _a, _b, _c, _d;
        var lockedItemCount = (_b = (_a = appBarContext.lockedItems) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        var pinnedItemCount = (_d = (_c = appBarContext.pinnedItems) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
        mediaQuery.setElementCount(lockedItemCount + pinnedItemCount);
    }, [appBarContext.lockedItems, appBarContext.pinnedItems, mediaQuery]);
    /**
     * Updates selection bar position and color
     */
    React.useEffect(function () {
        var _a, _b, _c;
        if (!selectionBar.current) {
            return;
        }
        var tileRef = (_b = (_a = appBarContext.hoistedItem) === null || _a === void 0 ? void 0 : _a.elementRef) !== null && _b !== void 0 ? _b : (_c = appBarContext.selectedItem) === null || _c === void 0 ? void 0 : _c.elementRef;
        var visibility = true;
        if (tileRef === null || tileRef === void 0 ? void 0 : tileRef.current) {
            selectionBar.current.setPosition(tileRef.current.offsetTop);
        }
        else if (!!(flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.selectFlyoutButtonWhenNoAppsSelected) &&
            flyoutButtonRef.current) {
            selectionBar.current.setPosition(flyoutButtonRef.current.offsetTop);
        }
        else {
            visibility = false;
        }
        selectionBar.current.setVisible(visibility);
    }, [
        selectionBar,
        flyoutButtonRef,
        (_l = appBarContext.selectedItem) === null || _l === void 0 ? void 0 : _l.elementRef,
        (_m = appBarContext.hoistedItem) === null || _m === void 0 ? void 0 : _m.elementRef,
        appBarContext.hoistedItem,
        flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.selectFlyoutButtonWhenNoAppsSelected,
    ]);
    /**
     * Callback to show the tile context menu
     *
     * @param target Target element to use as the context menu anchor
     * @param pinnedIndex Pinned index value of element or undefined if not pinned
     * @param commands Array of commands supported by the tile
     */
    var showContextMenu = React.useCallback(function (target, pinnedIndex, commands, targetAppTileId) {
        if (contextMenuRef === null || contextMenuRef === void 0 ? void 0 : contextMenuRef.current) {
            contextMenuRef.current.open(target, pinnedIndex, commands, targetAppTileId);
        }
    }, []);
    /**
     * Callback to manage the pin state changes of the launch tiles the AppBar hosts.
     * @param id The ID of the application that changed pin state.
     * @param pinStatus The new pin status of the application.
     */
    var pinStateChangedCallback = React.useCallback(function (id, pinStatus) {
        if (pinStatus) {
            pinApp(id);
            // After an app has been pinned focus it for keyboard users and ensure it is announced by screen readers
            appbarContainerRef.current &&
                waitForElement(id, 'button', { 'data-pinned': 'true' }, appbarContainerRef.current).then(function (element) { return element === null || element === void 0 ? void 0 : element.focus(); });
        }
        else {
            unpinApp(id);
        }
    }, [pinApp, unpinApp]);
    /**
     * Sets the overflowIndex in context based on the visibility
     * vector from the media query hook
     */
    React.useLayoutEffect(function () {
        var hiddenStart = mediaQuery.visibilityVector.indexOf(false);
        // if all values are true, overflow starts after the last tile
        if (hiddenStart === -1) {
            hiddenStart = mediaQuery.visibilityVector.length;
        }
        // don't set overflowIndex if we don't have a full visibility vector yet
        if (hiddenStart === 0) {
            return;
        }
        appBarContext.setOverflowIndex(hiddenStart);
    }, [appBarContext, mediaQuery.visibilityVector]);
    var novaEventing = useNovaEventing();
    /**
     * Builds a launch tile for display on the AppBar or Flyout
     *
     * @param m365AppEdge Tile data used to render tile
     * @param index Index of tile related to other tiles
     * @param mountTo Portal element to render to (eg. Flyout)
     */
    var renderLaunchTile = useCallback(function (appBarItem, index) {
        if (!appBarItem.data.node) {
            return null;
        }
        var launchTilePinStateChanged = function (pinStatus) {
            pinStateChangedCallback(appBarItem.data.node.id, pinStatus);
        };
        var m365App = appBarItem.data.node;
        var launchTile = tileBehavior && (React.createElement(LaunchTile, { index: index, viewMode: isAppbarWide ? ViewMode.Wide : ViewMode.Default, key: m365App.id, m365App: m365App, launchTileBehavior: tileBehavior, assignShortcutHandler: assignShortcutHandler, unassignShortcutHandler: unassignShortcutHandler, ref: appBarItem.itemRef, onContextMenu: showContextMenu, onPinStateChange: launchTilePinStateChanged, enableMicaV2DesktopStyles: enableMicaV2DesktopStyles, enableMicaV2WebStyles: enableMicaV2WebStyles, enableMercuryDesignStyles: enableMercuryDesignStyles, dragDropEnabled: !!tileBehavior.dragDropEnabled, backgroundShade: backgroundShade, enableVisualRefreshStyles: enableVisualRefreshStyles }));
        var onMouseEnter = function () {
            var _a;
            if (m365App.id === ((_a = appBarContext.selectedItem) === null || _a === void 0 ? void 0 : _a.data.node.id)) {
                // Fire the Nova event, then call the prop callback
                novaEventing.generateEvent({
                    event: AppBarEvents.appTileHover(function () {
                        var _a, _b;
                        var isHoisted = appBarContext.overflowIndex === index;
                        var selectedAppItem = isHoisted
                            ? appBarContext.hoistedItem
                            : appBarContext.selectedItem;
                        return {
                            id: m365App.id,
                            isHovered: true,
                            tilePosition: index,
                            tilePositionVertical: appTilePositionEnabled
                                ? (_b = (_a = selectedAppItem === null || selectedAppItem === void 0 ? void 0 : selectedAppItem.itemRef.current) === null || _a === void 0 ? void 0 : _a.getAppIconPositionRect()) === null || _b === void 0 ? void 0 : _b.top
                                : undefined,
                        };
                    }),
                });
                if (onAppTileHoverEnter) {
                    onAppTileHoverEnter();
                }
            }
        };
        var onMouseLeave = function () {
            var _a;
            if (m365App.id === ((_a = appBarContext.selectedItem) === null || _a === void 0 ? void 0 : _a.data.node.id)) {
                // Fire the Nova event, then call the prop callback
                novaEventing.generateEvent({
                    event: AppBarEvents.appTileHover(function () {
                        var _a, _b;
                        var isHoisted = appBarContext.overflowIndex === index;
                        var selectedAppItem = isHoisted
                            ? appBarContext.hoistedItem
                            : appBarContext.selectedItem;
                        return {
                            id: m365App.id,
                            isHovered: false,
                            tilePosition: index,
                            tilePositionVertical: appTilePositionEnabled
                                ? (_b = (_a = selectedAppItem === null || selectedAppItem === void 0 ? void 0 : selectedAppItem.itemRef.current) === null || _a === void 0 ? void 0 : _a.getAppIconPositionRect()) === null || _b === void 0 ? void 0 : _b.top
                                : undefined,
                        };
                    }),
                });
                if (onAppTileHoverLeave) {
                    onAppTileHoverLeave();
                }
            }
        };
        return (React.createElement("div", { key: m365App.id, ref: appBarItem.elementRef, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, launchTile));
    }, [
        tileBehavior,
        isAppbarWide,
        assignShortcutHandler,
        unassignShortcutHandler,
        showContextMenu,
        enableMicaV2DesktopStyles,
        enableMicaV2WebStyles,
        enableMercuryDesignStyles,
        backgroundShade,
        pinStateChangedCallback,
        appBarContext.selectedItem,
        appBarContext.hoistedItem,
        appBarContext.overflowIndex,
        novaEventing,
        onAppTileHoverEnter,
        appTilePositionEnabled,
        onAppTileHoverLeave,
    ]);
    var handleDragEnd = React.useCallback(function (movingApplicationID, movingToApplicationID) {
        // if the moving id and moving to id are the same then we don't need to run the pin logic
        // just set dndInUse state to false after the move has ended
        if (movingApplicationID !== movingToApplicationID) {
            movePinnedApp(movingApplicationID, movingToApplicationID);
        }
        // set dndInUse state to false after the move has ended
        setDnDInUse(false);
        // reset the divider visible state to true after move has ended
        setDividerVisibleState(true);
    }, [movePinnedApp]);
    var handleDragStart = React.useCallback(function () {
        // apply effects for when dnd has been started activated by the user
        // set dnd in use to true on move
        setDnDInUse(true);
        setDividerVisibleState(false);
    }, []);
    var handleDragCancel = React.useCallback(function () {
        // apply effects for when dnd has been cancelled by the user
        // set dnd in use to false on cancel
        setDnDInUse(false);
        setDividerVisibleState(true);
    }, []);
    /**
     * Callback to render the locked launch tiles when launch tile data is updated
     */
    var renderLockedItems = useCallback(function () {
        var _a;
        var visibleTiles = (_a = appBarContext.lockedItems) === null || _a === void 0 ? void 0 : _a.slice(0, appBarContext.overflowIndex);
        return visibleTiles === null || visibleTiles === void 0 ? void 0 : visibleTiles.map(function (tile, index) {
            return renderLaunchTile(tile, index);
        });
    }, [
        appBarContext.lockedItems,
        appBarContext.overflowIndex,
        renderLaunchTile,
    ]);
    /**
     * Callback to render the anchored launch tiles when launch tile data is updated
     */
    var renderAnchoredItems = useCallback(function () {
        var _a;
        return (_a = appBarContext.anchoredItems) === null || _a === void 0 ? void 0 : _a.map(function (tile, index) {
            var _a;
            return renderLaunchTile(tile, index + ((_a = appBarContext.overflowIndex) !== null && _a !== void 0 ? _a : 0));
        });
    }, [
        appBarContext.anchoredItems,
        appBarContext.overflowIndex,
        renderLaunchTile,
    ]);
    var renderPinnedItems = useCallback(function () {
        var _a;
        if (!(appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.lockedItems)) {
            return;
        }
        var startIndex = appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.lockedItems.length;
        if (appBarContext.overflowIndex !== undefined &&
            appBarContext.overflowIndex < startIndex) {
            return;
        }
        var offsetOverflow = appBarContext.overflowIndex !== undefined
            ? appBarContext.overflowIndex - appBarContext.lockedItems.length
            : undefined;
        var visibleTiles = (_a = appBarContext.pinnedItems) === null || _a === void 0 ? void 0 : _a.slice(0, offsetOverflow);
        return visibleTiles === null || visibleTiles === void 0 ? void 0 : visibleTiles.map(function (tile, index) {
            return renderLaunchTile(tile, index + startIndex);
        });
    }, [
        appBarContext.lockedItems,
        appBarContext.overflowIndex,
        appBarContext.pinnedItems,
        renderLaunchTile,
    ]);
    /**
     * Callback to render the hoisted launchtile if present
     */
    var renderHoistedItem = React.useCallback(function () {
        var _a;
        // get the correct index for the hoisted item
        var hoistedItemIndex = (_a = appBarContext.overflowIndex) !== null && _a !== void 0 ? _a : 0;
        if (appBarContext.hoistedItem) {
            return renderLaunchTile(appBarContext.hoistedItem, hoistedItemIndex);
        }
        return undefined;
    }, [
        appBarContext.hoistedItem,
        appBarContext.overflowIndex,
        renderLaunchTile,
    ]);
    /**
     * Callback used by AppBar consumers to dispatch actions after render
     */
    React.useImperativeHandle(forwardRef, function () { return ({
        focus: function () {
            var _a, _b;
            (_b = (_a = appBarContext.selectedItem) === null || _a === void 0 ? void 0 : _a.itemRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        },
        getAppIconPositionRect: function (id) {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (!id || id === ((_a = appBarContext.selectedItem) === null || _a === void 0 ? void 0 : _a.data.node.id)) {
                return (_c = (_b = appBarContext.selectedItem) === null || _b === void 0 ? void 0 : _b.itemRef.current) === null || _c === void 0 ? void 0 : _c.getAppIconPositionRect();
            }
            return (_h = (_g = (_f = (_d = appBarContext.lockedItems) === null || _d === void 0 ? void 0 : _d.concat((_e = appBarContext.pinnedItems) !== null && _e !== void 0 ? _e : [])) === null || _f === void 0 ? void 0 : _f.find(function (item) {
                return item.data.node.id === id;
            })) === null || _g === void 0 ? void 0 : _g.itemRef.current) === null || _h === void 0 ? void 0 : _h.getAppIconPositionRect();
        },
        getAppIconElementRef: function (id) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            if (!id || id === ((_a = appBarContext.selectedItem) === null || _a === void 0 ? void 0 : _a.data.node.id)) {
                return (_d = (_c = (_b = appBarContext.selectedItem) === null || _b === void 0 ? void 0 : _b.itemRef) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.getAppIconElementRef();
            }
            return id === FlyoutTileID
                ? flyoutButtonRef
                : (_k = (_j = (_h = (_g = (_e = appBarContext.lockedItems) === null || _e === void 0 ? void 0 : _e.concat((_f = appBarContext.pinnedItems) !== null && _f !== void 0 ? _f : [])) === null || _g === void 0 ? void 0 : _g.find(function (item) {
                    return item.data.node.id === id;
                })) === null || _h === void 0 ? void 0 : _h.itemRef) === null || _j === void 0 ? void 0 : _j.current) === null || _k === void 0 ? void 0 : _k.getAppIconElementRef();
        },
    }); }, [
        flyoutButtonRef,
        appBarContext.lockedItems,
        appBarContext.pinnedItems,
        (_o = appBarContext.selectedItem) === null || _o === void 0 ? void 0 : _o.data.node.id,
        (_p = appBarContext.selectedItem) === null || _p === void 0 ? void 0 : _p.itemRef,
    ]);
    var appBarSortableContent = React.useMemo(function () {
        return (React.createElement(React.Fragment, null,
            renderPinnedItems(),
            appBarContext.hoistedItem && !enableAIHub && (React.createElement(React.Fragment, null,
                dividerVisibleState && (React.createElement(Divider, { inset: true, className: dividerStyles })),
                React.createElement("div", null, renderHoistedItem())))));
    }, [
        appBarContext.hoistedItem,
        dividerStyles,
        dividerVisibleState,
        enableAIHub,
        renderHoistedItem,
        renderPinnedItems,
    ]);
    // Lazy renderer for the DnDContext
    var LazyDnDContextRenderer = React.useRef(React.lazy(function () {
        return customImportHandler(import(
        /* webpackChunkName: "appbar-control-lazydndcontext" */ './LazyDnDContext'));
    }));
    var sortableItems = React.useMemo(function () {
        var LazyDnDContext = LazyDnDContextRenderer.current;
        var a11yStrings = {
            onDragStartText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragStartText) || '',
            onDragEndText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragEndText) || '',
            onDragOverText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragOverText) || '',
            onDragCancelText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragCancelText) || '',
            onDragAtTopText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragAtTopText) || '',
            onDragAtBottomText: (controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.onDragAtBottomText) || '',
        };
        return appBarContext.pinnedItems &&
            appBarContext.pinnedItems.length > 0 &&
            (tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.dragDropEnabled) ? (React.createElement(AppBarErrorBoundary, { boundarySource: "AppbarDnDContext" },
            React.createElement(React.Suspense, { fallback: "" },
                React.createElement(LazyDnDContext, { sortableItemsById: appBarItemsById, appbarItemsByIdLookUp: appbarItemsByIdLookUp, handleDragEndCallback: handleDragEnd, handleDragStartCallback: handleDragStart, handleDragCancelCallback: handleDragCancel, a11yStrings: a11yStrings }, appBarSortableContent)))) : (appBarSortableContent);
    }, [
        appBarContext.pinnedItems,
        appBarItemsById,
        appbarItemsByIdLookUp,
        appBarSortableContent,
        handleDragCancel,
        handleDragEnd,
        handleDragStart,
        tileBehavior === null || tileBehavior === void 0 ? void 0 : tileBehavior.dragDropEnabled,
        controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings,
    ]);
    var showFlyoutButton = (appBarContext.overflowItems &&
        appBarContext.overflowItems.length > 0) ||
        (((_r = (_q = appBarContext.appBarData) === null || _q === void 0 ? void 0 : _q.m365Apps) === null || _r === void 0 ? void 0 : _r.appAcquisitionLinks) &&
            ((_u = (_t = (_s = appBarContext.appBarData) === null || _s === void 0 ? void 0 : _s.m365Apps) === null || _t === void 0 ? void 0 : _t.appAcquisitionLinks) === null || _u === void 0 ? void 0 : _u.length) >
                0);
    // Enable the tile selection bar
    var enableTileSelectionBar = !dndInUse &&
        appBarContext.selectedItem &&
        !enableMercuryDesignStyles &&
        !enableVisualRefreshStyles;
    return React.useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23;
        return (React.createElement(React.Fragment, null, controlModelData && tileBehavior && (React.createElement(FlexPanel, { ref: setAppBarRef, transparent: !!tileBehavior.transparent, className: appBarStyles, backgroundShadeClass: backgroundShadeStyle, keyNavigationDirection: dndInUse ? undefined : KeyNavDir.vertical, memorizeCurrent: true },
            React.createElement(FlexPanel, { className: getAppBarClassname(isAppbarWide, enableAIHub || enableVisualRefreshStyles, styles), role: "navigation", transparent: !!tileBehavior.transparent, "aria-label": controlModelData.rootNavigationAriaLabel ||
                    undefined, mercuryDesignEnabled: enableMercuryDesignStyles },
                enableBrandLogo && (React.createElement("div", { className: mergeClasses(styles.tileContainer, isAppbarWide &&
                        styles.tileContainerWide) }, brandLogo)),
                React.createElement(LazyTileContextMenu, { ref: contextMenuRef, tileContextMenu: tileBehavior.tileContextMenu, customImportHandler: customImportHandler },
                    React.createElement(React.Fragment, null,
                        renderLockedItems(),
                        sortableItems))),
            React.createElement(FlexPanel, { fill: true, transparent: !!tileBehavior.transparent },
                showFlyoutButton && (React.createElement(FlyoutButton, { customImportHandler: customImportHandler, selected: !appBarContext.selectedItem &&
                        !!(flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.selectFlyoutButtonWhenNoAppsSelected), ref: flyoutButtonRef, icon: flyoutIcon, selectedIcon: flyoutIconSelected, text: (_a = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutButtonText) !== null && _a !== void 0 ? _a : '', title: (_b = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutButtonTitle) !== null && _b !== void 0 ? _b : '', emptyStateMessage: (_c = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutEmptyStateMessage) !== null && _c !== void 0 ? _c : '', searchBoxPlaceholderText: (_f = (_e = (_d = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _d === void 0 ? void 0 : _d.searchBehavior) === null || _e === void 0 ? void 0 : _e.placeholderText) !== null && _f !== void 0 ? _f : '', suggestedAppsPrivacyLinkText: (_g = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsPrivacyLinkText) !== null && _g !== void 0 ? _g : '', suggestedAppsPermissionsLinkText: (_h = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsPermissionsLinkText) !== null && _h !== void 0 ? _h : '', suggestedAppsTermsOfUseLinkText: (_j = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsTermsOfUseLinkText) !== null && _j !== void 0 ? _j : '', suggestedAppsAddButtonText: (_k = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsAddButtonText) !== null && _k !== void 0 ? _k : '', suggestedAppsSectionTitle: (_l = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsSectionTitle) !== null && _l !== void 0 ? _l : undefined, quickAccessSectionTitle: (_m = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutQuickAccessSectionTitle) !== null && _m !== void 0 ? _m : undefined, seeAllButtonLabel: (_o = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSeeAllButtonLabel) !== null && _o !== void 0 ? _o : '', installedAppsAnnouncementMessage: (_p = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutInstalledAppsSearchResultsAnnouncement) !== null && _p !== void 0 ? _p : undefined, suggestedAppsAnnouncementMessage: (_q = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutSuggestedAppsSearchResultsAnnouncement) !== null && _q !== void 0 ? _q : undefined, installedAndSuggestedAppsAnnouncementMessage: (_r = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings.flyoutInstalledAndSuggestedAppsSearchResultsAnnouncement) !== null && _r !== void 0 ? _r : undefined, wide: isAppbarWide, footerLinks: (_t = (_s = appBarContext.appBarData) === null || _s === void 0 ? void 0 : _s.m365Apps) === null || _t === void 0 ? void 0 : _t.appAcquisitionLinks, enableVisualRefreshStyles: enableVisualRefreshStyles, launchTileBehavior: tileBehavior, flyoutSize: (flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.flyoutSize) || 'Large', enabledFlyoutButtonBadgeType: ((_u = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _u === void 0 ? void 0 : _u.enabledFlyoutButtonBadgeType) ||
                        'None', flyoutButtonBadgeDescription: (_w = (_v = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings) === null || _v === void 0 ? void 0 : _v.flyoutButtonBadgeDescription) !== null && _w !== void 0 ? _w : undefined, footerLinkFormat: (_x = linkBehavior === null || linkBehavior === void 0 ? void 0 : linkBehavior.linkFormat) !== null && _x !== void 0 ? _x : undefined, searchEnabled: !!((_z = (_y = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _y === void 0 ? void 0 : _y.searchBehavior) === null || _z === void 0 ? void 0 : _z.isEnabled), suggestionsEnabled: !!((_0 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _0 === void 0 ? void 0 : _0.isSuggestionsSectionEnabled), quickAccessEnabled: !!((_1 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _1 === void 0 ? void 0 : _1.isQuickAccessSectionEnabled), seeAllButtonEnabled: !!((_2 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _2 === void 0 ? void 0 : _2.isSeeAllButtonEnabled), seeAllButtonIndex: (_3 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _3 === void 0 ? void 0 : _3.seeAllButtonIndex, searchDebounceRate: (_5 = (_4 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _4 === void 0 ? void 0 : _4.searchBehavior) === null || _5 === void 0 ? void 0 : _5.debounceRate, searchNoResultsTitle: (_7 = (_6 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _6 === void 0 ? void 0 : _6.searchBehavior) === null || _7 === void 0 ? void 0 : _7.noResultsTitle, searchNoResultsMessage: (_9 = (_8 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _8 === void 0 ? void 0 : _8.searchBehavior) === null || _9 === void 0 ? void 0 : _9.noResultsMessage, searchResultsSectionTitle: (_12 = (_11 = (_10 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _10 === void 0 ? void 0 : _10.searchBehavior) === null || _11 === void 0 ? void 0 : _11.resultsSectionTitle) !== null && _12 !== void 0 ? _12 : undefined, quickAccessSearchResultsSectionTitle: (_15 = (_14 = (_13 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.flyoutBehavior) === null || _13 === void 0 ? void 0 : _13.searchBehavior) === null || _14 === void 0 ? void 0 : _14.quickAccessResultsSectionTitle) !== null && _15 !== void 0 ? _15 : undefined, installedAppsSectionAriaLabel: (_17 = (_16 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings) === null || _16 === void 0 ? void 0 : _16.flyoutInstalledAppsSectionAriaLabel) !== null && _17 !== void 0 ? _17 : undefined, searchResultsAriaLiveAnnouncement: (_19 = (_18 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings) === null || _18 === void 0 ? void 0 : _18.flyoutSearchResultsAriaLiveAnnouncement) !== null && _19 !== void 0 ? _19 : undefined, suggestedAppsSectionAriaLabel: (_21 = (_20 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings) === null || _20 === void 0 ? void 0 : _20.flyoutSuggestedAppsSectionAriaLabel) !== null && _21 !== void 0 ? _21 : undefined, quickAccessSectionAriaLabel: (_23 = (_22 = controlModelData === null || controlModelData === void 0 ? void 0 : controlModelData.localizedStrings) === null || _22 === void 0 ? void 0 : _22.flyoutQuickAccessSectionAriaLabel) !== null && _23 !== void 0 ? _23 : undefined, onPinStateChange: pinStateChangedCallback })),
                renderAnchoredItems()),
            enableTileSelectionBar && (React.createElement(TileSelectionBar, { wide: isAppbarWide, animate: !!controlModelData.animateSelection, initialVisibility: false, ref: selectionBar })),
            React.createElement(AppBarImpression, { impressionStabilizeTimeout: controlModelData.impressionStabilizeTimeout ||
                    undefined })))));
    }, [
        controlModelData,
        tileBehavior,
        setAppBarRef,
        appBarStyles,
        backgroundShadeStyle,
        dndInUse,
        isAppbarWide,
        enableAIHub,
        enableBrandLogo,
        styles,
        enableMercuryDesignStyles,
        enableVisualRefreshStyles,
        customImportHandler,
        renderLockedItems,
        sortableItems,
        showFlyoutButton,
        appBarContext.selectedItem,
        (_w = (_v = appBarContext.appBarData) === null || _v === void 0 ? void 0 : _v.m365Apps) === null || _w === void 0 ? void 0 : _w.appAcquisitionLinks,
        flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.selectFlyoutButtonWhenNoAppsSelected,
        flyoutBehavior === null || flyoutBehavior === void 0 ? void 0 : flyoutBehavior.flyoutSize,
        flyoutIcon,
        flyoutIconSelected,
        linkBehavior === null || linkBehavior === void 0 ? void 0 : linkBehavior.linkFormat,
        pinStateChangedCallback,
        renderAnchoredItems,
        selectionBar,
    ]);
};
/**
 * Encapsulates and provides context to the main AppBar render object
 * @param props Props for the AppBar control
 * @param ref Forward ref to provide additional functionality for hosting apps
 * @returns Forward ref render function for AppBar with context
 */
export var AppBarWithContext = function (props, ref) {
    return (React.createElement(AppBarErrorBoundary, { boundarySource: "Appbar" },
        React.createElement(AppBarContextProvider, null,
            React.createElement(AppBarRender, __assign({}, props, { forwardRef: ref })))));
};
export var AppBar = React.forwardRef(AppBarWithContext);
var calculateTileHeight = function (isAppbarWide, enableMercuryDesignStyles) {
    return isAppbarWide
        ? TileHeightWideValue
        : enableMercuryDesignStyles
            ? TileHeightMercuryValue
            : TileDimensionDefaultValue + 2 * TileMarginDefaultValue;
};
function getAppBarClassname(isAppbarWide, shouldCenterAlign, styles) {
    if (shouldCenterAlign)
        return isAppbarWide
            ? styles.tileContainerWideCenterAligned
            : styles.tileContainerCenterAligned;
    if (isAppbarWide)
        return styles.tileContainerWide;
    else
        return styles.tileContainer;
}
// Uses MutationObserver to wait for an element to be added to the DOM
function waitForElement(id, role, additionalAttributes, target) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve) {
                    var observer = new MutationObserver(function () {
                        var element = document.getElementById(id);
                        if (element &&
                            element.getAttribute('role') === role &&
                            Object.keys(additionalAttributes).every(function (key) {
                                return element.getAttribute(key) === additionalAttributes[key];
                            })) {
                            observer.disconnect();
                            resolve(element);
                        }
                    });
                    observer.observe(target, {
                        childList: true,
                        subtree: true,
                    });
                    // Back up to ensure the observer is disconnected
                    var maxTimeout = 3000;
                    setTimeout(function () {
                        observer.disconnect();
                        resolve(null);
                    }, maxTimeout);
                })];
        });
    });
}
//# sourceMappingURL=AppBar.js.map